<script setup lang="ts">
/*
* Компонент LinksSettings - Раздел «Настройки полезных ссылок» (/links/settings)
*
* Ссылки с мейн-портала (`is_daomed_main`) нельзя редактировать и удалять:
* доступны только активность и закрепление.
*/
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'

import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import LinkFormModal from '@/modules/links/components/settings/LinkFormModal.vue'
import { useLinksSettingsStore } from '@/modules/links/stores/linksSettingsStore'
import type { IUsefulLink } from '@/modules/links/types/IUsefulLink'
import emptyLink from '@images/links/emptyLink.jpg'

const { xs } = useDisplay()
const linksSettingsStore = useLinksSettingsStore()

const localSearch = ref('')

onMounted(() => {
  linksSettingsStore.filters.search = ''
  void linksSettingsStore.fetchLinks()
})

watchDebounced(localSearch, val => {
  linksSettingsStore.filters.search = val ?? ''
  void linksSettingsStore.fetchLinks()
}, { debounce: 400 })

const headers = [
  { title: 'Наименование', key: 'name', sortable: false },
  { title: 'Активность', key: 'is_active', sortable: false },
  { title: 'Действия', key: 'actions', width: '140px', sortable: false, align: 'end' },
] as const

function getRowProps({ item }: { item: IUsefulLink }) {
  return item.is_daomed_main ? { class: 'link-row--locked' } : {}
}

const isFormOpen = ref(false)
const selectedLink = ref<IUsefulLink | null>(null)

const openCreate = () => {
  selectedLink.value = null
  isFormOpen.value = true
}

const openEdit = (link: IUsefulLink) => {
  if (link.is_daomed_main) return
  selectedLink.value = link
  isFormOpen.value = true
}

function onRowClick(_: unknown, row: { item: IUsefulLink }) {
  openEdit(row.item)
}

const toggleActive = async (link: IUsefulLink, isActive: boolean) => {
  if (link.is_active === isActive) return
  await linksSettingsStore.toggleLinkActive(link.id, isActive)
}

const showDeleteDialog = ref(false)
const linkToDelete = ref<IUsefulLink | null>(null)

const requestDelete = (link: IUsefulLink) => {
  linkToDelete.value = link
  showDeleteDialog.value = true
}

const confirmDelete = async (confirmed: boolean) => {
  showDeleteDialog.value = false
  const link = linkToDelete.value
  linkToDelete.value = null
  if (!confirmed || !link) return
  await linksSettingsStore.deleteLink(link.id)
}

const loadMore = () => {
  if (linksSettingsStore.nextCursor)
    void linksSettingsStore.fetchLinks(linksSettingsStore.nextCursor)
}
</script>

<template>
  <VCard class="border overflow-hidden" elevation="0">
    <div class="d-flex align-center justify-space-between gap-4 pa-4" :class="{ 'flex-column': xs }">
      <VTextField
        v-model="localSearch"
        variant="outlined"
        clearable
        placeholder="Найти..."
        append-inner-icon="ri-search-line"
        hide-details
        class="flex-grow-0"
        :class="xs ? 'w-100' : 'w-25'"
      />
      <VBtn
        :block="xs"
        variant="elevated"
        color="primary"
        prepend-icon="ri-add-line"
        @click="openCreate"
      >
        Добавить ссылку
      </VBtn>
    </div>

    <VDataTable
      :headers="headers"
      :items="linksSettingsStore.links"
      :items-per-page="-1"
      :row-props="getRowProps"
      class="no-row-gaps cursor-pointer-table custom-data-table"
      hide-default-footer
      @click:row="onRowClick"
    >
      <template #no-data>
        <div class="text-center text-italic py-4 text-medium-emphasis">
          Ссылки не найдены
        </div>
      </template>
      <template #[`item.name`]="{ item }">
        <div class="d-flex align-center gap-3 py-2">
          <VImg
            :src="item.logo || emptyLink"
            width="40"
            height="40"
            class="flex-grow-0"
          />
          <div class="d-flex flex-column">
            <span class="font-weight-medium">{{ item.name }}</span>
            <span class="text-caption text-medium-emphasis text-break">{{ item.url }}</span>
          </div>
        </div>
      </template>

      <template #[`header.is_active`]="{ column }">
        <div class="d-flex align-center ga-1">
          <span>{{ column.title }}</span>
          <VTooltip location="top">
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="d-inline-flex activity-info-icon"
              >
                <VIcon
                  icon="ri-information-line"
                  :size="16"
                  color="secondary"
                />
              </span>
            </template>
            При включенной активности ссылка отображается в разделе «Полезные ссылки»
          </VTooltip>
        </div>
      </template>

      <template #[`item.is_active`]="{ item }">
        <div @click.stop>
          <VSwitch
            :model-value="item.is_active"
            color="primary"
            hide-details
            density="compact"
            inset
            @update:model-value="toggleActive(item, Boolean($event))"
          />
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end ga-3">
          <VTooltip location="top">
            <template #activator="{ props: tipProps }">
              <VIconBtn
                v-bind="tipProps"
                :rounded="false"
                :size="32"
                :icon="item.is_pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'"
                variant="text"
                :color="item.is_pinned ? 'primary' : 'secondary'"
                @click.stop="linksSettingsStore.toggleLinkPin(item.id)"
              />
            </template>
            {{ item.is_pinned ? 'Открепить' : 'Закрепить: ссылка всегда в избранном у всех' }}
          </VTooltip>
          <VIconBtn
            v-if="!item.is_daomed_main"
            :rounded="false"
            :size="32"
            icon="ri-delete-bin-3-line"
            variant="text"
            color="secondary"
            @click.stop="requestDelete(item)"
          />
          <VIconBtn
            v-if="!item.is_daomed_main"
            :rounded="false"
            :size="32"
            icon="ri-edit-line"
            variant="text"
            color="secondary"
            @click.stop="openEdit(item)"
          />
        </div>
      </template>
    </VDataTable>

    <div v-if="linksSettingsStore.nextCursor" class="d-flex justify-center pa-4">
      <VBtn
        variant="outlined"
        color="secondary"
        @click="loadMore"
      >
        Показать ещё
      </VBtn>
    </div>
  </VCard>

  <LinkFormModal
    v-model="isFormOpen"
    :link="selectedLink"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="showDeleteDialog"
    confirmation-question="Удалить ссылку?"
    :confirmation-text="linkToDelete
      ? `Вы действительно хотите удалить ссылку «${linkToDelete.name}»? Это действие нельзя отменить.`
      : 'Это действие нельзя отменить.'"
    confirm-btn="Удалить"
    reject-btn="Отмена"
    @confirm="confirmDelete"
  />
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

/* Шапка таблицы 15px; VIcon по умолчанию 1.5em (~22px). Макет — 16×16. */
.activity-info-icon :deep(i) {
  font-size: 16px !important;
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

/* Ссылки с мейн-портала — только активность и закрепление, форма не открывается */
:deep(.custom-data-table tr.link-row--locked) {
  cursor: default;
}

:deep(.custom-data-table tr.link-row--locked:hover) {
  box-shadow: none;
  cursor: default;
  background-color: transparent !important;
}

:deep(.custom-data-table tr.link-row--locked .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity)) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}
</style>
